import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { ROLE_LABELS } from "@book2class/shared";
import { api, ApiError } from "../api";
import { isTeacher, useAuth } from "../auth";
import { BrandMark, Spinner, ThemeToggle } from "./ui";
import { BadgeToaster, FontSizeControl, OfflineBanner } from "./extras";

/** Tela dividida: painel ilustrativo (desktop) e formulário. */
export function AuthLayout({
  children,
  title,
  subtitle,
}: {
  children: ReactNode;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="auth">
      <aside className="auth-art" aria-hidden>
        <div className="brand" style={{ color: "#fdfbf6" }}>
          <BrandMark /> book2class
        </div>
        <div>
          <h2>Do ebook ao curso, sem perder o essencial.</h2>
          <p>
            Envie o PDF, revise o que foi gerado e publique uma trilha com aulas, exercícios, flashcards e
            certificado.
          </p>
        </div>
        <svg className="pages" viewBox="0 0 200 160" fill="none" stroke="#fdfbf6" strokeWidth="1.4">
          {Array.from({ length: 9 }, (_, i) => (
            <path
              key={i}
              d={`M${20 + i * 4} ${30 - i * 2}q40-14 80 6q40-20 80-6v110q-40-14-80 6q-40-20-80-6z`}
            />
          ))}
        </svg>
      </aside>
      <main className="auth-main">
        <div className="auth-card">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 28,
            }}
          >
            <Link to="/" className="brand" style={{ marginBottom: 0 }}>
              <BrandMark /> book2class
            </Link>
            <ThemeToggle />
          </div>
          <h1>{title}</h1>
          {subtitle && <p style={{ color: "var(--ink-2)", marginBottom: 24 }}>{subtitle}</p>}
          {children}
        </div>
      </main>
    </div>
  );
}

/** Rotas internas: exige sessão e, quando indicado, perfil de professor. */
export function AppLayout({
  teacherOnly = false,
  adminOnly = false,
}: {
  teacherOnly?: boolean;
  adminOnly?: boolean;
}) {
  const { user, loading, logout, pendingConsents } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // O menu do celular vale para a página em que foi aberto e fecha sozinho na navegação.
  const [menuPath, setMenuPath] = useState<string | null>(null);
  const menuOpen = menuPath === location.pathname;

  if (loading)
    return (
      <div className="center">
        <Spinner />
      </div>
    );
  if (!user) return <Navigate to={`/entrar?voltar=${encodeURIComponent(location.pathname)}`} replace />;
  if (teacherOnly && !isTeacher(user)) return <Navigate to="/aluno" replace />;
  if (adminOnly && user.role !== "admin" && !user.isPlatformAdmin) return <Navigate to="/" replace />;

  const brandName = user.institution.branding.displayName || user.institution.name;
  const logo = user.institution.branding.logoUrl;
  const isAdmin = user.role === "admin" || user.isPlatformAdmin;
  // Itens de navegação conforme o perfil; os mesmos aparecem no menu do celular.
  const navLinks = (
    <>
      {isTeacher(user) && (
        <>
          <NavLink to="/professor/cursos">Cursos</NavLink>
          <NavLink to="/professor/ebooks">Ebooks</NavLink>
          <NavLink to="/professor/estudio">Estúdio</NavLink>
        </>
      )}
      <NavLink to="/aluno" end>
        {isTeacher(user) ? "Estudar" : "Meus cursos"}
      </NavLink>
      {isAdmin && <NavLink to="/admin">Administração</NavLink>}
    </>
  );
  return (
    <>
      <a href="#conteudo" className="skip-link">
        Pular para o conteúdo
      </a>
      <header className="topbar">
        <Link to="/" className="brand">
          {logo ? <img src={logo} alt="" className="brand-logo" /> : <BrandMark />}
          <span className="brand-name">{brandName}</span>
        </Link>
        <nav aria-label="Principal" hidden={user.pending}>
          {navLinks}
        </nav>
        <div className="spacer" />
        <Link to="/conta" className="user-chip" title="Minha conta" aria-label={`Minha conta: ${user.name}`}>
          <span className="user-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" strokeLinecap="round" />
            </svg>
          </span>
          <span className="user-text">
            <strong>{user.name}</strong>
            {user.isPlatformAdmin
              ? "Administrador da plataforma"
              : user.pending
                ? "Aguardando liberação"
                : ROLE_LABELS[user.role]}
          </span>
        </Link>
        <FontSizeControl />
        <ThemeToggle />
        <button
          type="button"
          className="btn ghost small topbar-logout"
          onClick={async () => {
            await logout();
            navigate("/entrar");
          }}
        >
          Sair
        </button>
        <button
          type="button"
          className="menu-toggle"
          aria-expanded={menuOpen}
          aria-controls="menu-celular"
          aria-label={menuOpen ? "Fechar o menu" : "Abrir o menu"}
          onClick={() => setMenuPath(menuOpen ? null : location.pathname)}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {menuOpen ? (
              <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
            ) : (
              <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
            )}
          </svg>
        </button>
      </header>
      {menuOpen && (
        <nav id="menu-celular" className="mobile-menu" aria-label="Menu">
          <div className="mobile-menu-user">
            <strong>{user.name}</strong>
            <code>{user.code}</code>
            <span>
              {user.isPlatformAdmin
                ? "Administrador da plataforma"
                : user.pending
                  ? "Aguardando liberação"
                  : ROLE_LABELS[user.role]}
            </span>
          </div>
          {!user.pending && navLinks}
          <NavLink to="/conta">Minha conta</NavLink>
          <div className="mobile-menu-tools">
            <FontSizeControl />
            <ThemeToggle />
          </div>
          <button
            type="button"
            className="btn ghost block"
            onClick={async () => {
              await logout();
              navigate("/entrar");
            }}
          >
            Sair
          </button>
        </nav>
      )}
      <div id="conteudo" tabIndex={-1} className="content-anchor">
        {user.pending ? <PendingAccess /> : <Outlet />}
      </div>
      <BadgeToaster />
      <OfflineBanner />
      {pendingConsents.length > 0 && <ConsentDialog />}
    </>
  );
}

/** Conta criada sem convite: aguarda o administrador definir o perfil e a instituição. */
function PendingAccess() {
  const { user, refresh } = useAuth();
  return (
    <main className="page narrow">
      <section className="card card-pad">
        <div className="eyebrow">Acesso solicitado</div>
        <h1>Seu acesso está em análise</h1>
        <p>
          Recebemos a sua solicitação, {user?.name}. O administrador vai definir o seu perfil e liberar o
          acesso aos cursos. Assim que isso acontecer, você recebe um aviso em {user?.email}.
        </p>
        <p className="muted">Se recebeu um link de convite, abra o link para entrar direto no curso.</p>
        <button type="button" className="btn ghost" onClick={() => void refresh()}>
          Verificar novamente
        </button>
      </section>
    </main>
  );
}

/** Solicita o aceite de novas versões dos termos ou da política de privacidade. */
function ConsentDialog() {
  const { pendingConsents, refresh, logout } = useAuth();
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [docs, setDocs] = useState<Array<{ id: string; title: string; version: string; content: string }>>(
    [],
  );
  const [readToEnd, setReadToEnd] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const names = pendingConsents.map((d) => `${d.title} (versão ${d.version})`).join(" e ");

  useEffect(() => {
    const ids = new Set(pendingConsents.map((d) => d.id));
    api
      .get<Array<{ id: string; title: string; version: string; content: string }>>("/legal/current")
      .then((all) => setDocs(all.filter((d) => ids.has(d.id))))
      .catch(() => setDocs([]));
  }, [pendingConsents]);

  // O aceite fica disponível depois da leitura até o fim (ou de imediato, quando o texto cabe sem rolagem).
  const checkEnd = () => {
    const el = box.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 8) setReadToEnd(true);
  };
  useEffect(checkEnd, [docs]);

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="consent-title">
      <div className="modal card card-pad consent-modal">
        <h2 id="consent-title">Atualizamos nossos documentos</h2>
        <p style={{ color: "var(--ink-2)" }}>
          Para continuar usando a plataforma, leia e aceite: {names}. Role o texto até o final para liberar o
          aceite.
        </p>
        <div
          ref={box}
          className="consent-text legal-text"
          tabIndex={0}
          aria-label="Texto dos documentos atualizados"
          onScroll={checkEnd}
        >
          {docs.length === 0 ? (
            <Spinner />
          ) : (
            docs.map((d) => (
              <section key={d.id}>
                <h3>
                  {d.title} · versão {d.version}
                </h3>
                {d.content}
              </section>
            ))
          )}
        </div>
        <p className="segment-meta" style={{ margin: "6px 0 10px" }}>
          {readToEnd ? "Leitura concluída." : "Continue rolando até o final do texto."} Os documentos também
          ficam disponíveis em{" "}
          <Link to="/documentos" target="_blank">
            documentos legais
          </Link>
          .
        </p>
        <label className="check" aria-disabled={!readToEnd}>
          <input
            type="checkbox"
            checked={checked}
            disabled={!readToEnd}
            onChange={(e) => setChecked(e.target.checked)}
          />
          Li e aceito os documentos atualizados.
        </label>
        {error && (
          <p className="alert error" style={{ marginTop: 12 }}>
            {error}
          </p>
        )}
        <div className="actions" style={{ marginTop: 20 }}>
          <button type="button" className="btn ghost" onClick={() => void logout()}>
            Sair
          </button>
          <button
            type="button"
            className="btn"
            disabled={!checked || !readToEnd || busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.post("/auth/consents", { documentIds: pendingConsents.map((d) => d.id) });
                await refresh();
              } catch (err) {
                setError(err instanceof ApiError ? err.message : "Não foi possível registrar o aceite.");
              } finally {
                setBusy(false);
              }
            }}
          >
            Aceitar e continuar
          </button>
        </div>
      </div>
    </div>
  );
}
